import React, { useEffect, useState, useRef, useCallback, Component, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useTTS } from '../../hooks/useTTS';
import TTSControls from '../../components/tts/TTSControls';

// 错误边界：防止 TTS 组件崩溃影响 koodo-reader
class TTSErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) { console.warn('[TTS] ErrorBoundary 捕获:', error.message); }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

interface BookInfo {
  id: number;
  title: string;
  author: string;
  cover?: string;
  filePath: string;
  format: string;
}

const Reader: React.FC = () => {
  console.log('[TTS] Reader 组件渲染');
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<BookInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [showTTS, setShowTTS] = useState(false);
  const [minimizedTTS, setMinimizedTTS] = useState(false);
  const [btnCentered, setBtnCentered] = useState(true);
  const [btnDragging, setBtnDragging] = useState(false);
  const [btnDragOffset, setBtnDragOffset] = useState({ x: 0, y: 0 });
  const btnPosRef = useRef({ x: 0, y: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  // 使用 TTS Hook
  const { ttsService, state: ttsState, speak, isSupported } = useTTS({
    autoWarmup: true,
  });

  useEffect(() => {
    fetchBookInfo();
  }, [bookId]);

  const fetchBookInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/books/' + bookId);
      setBook(data);
    } catch (err: any) {
      setError(err.response?.data?.message || '获取图书信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取 koodo-reader 的 URL - 使用 Blob URL 加载图书
  const getReaderUrl = async () => {
    if (!book) return '';
    
    try {
      // 固定使用 3001 端口访问 API
      const bookUrl = window.location.protocol + '//' + window.location.hostname + ':3001' + `/api/books/file/${book.id}`;
      
      const response = await fetch(bookUrl);
      
      if (!response.ok) {
        throw new Error('HTTP ' + response.status + ': ' + response.statusText);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // 将 Blob URL 和图书信息保存到 localStorage
      const bookData = {
        key: 'book_' + bookId,
        name: book.title,
        author: book.author || '',
        description: '',
        md5: '',
        cover: book.cover || '',
        format: book.format.toUpperCase(),
        publisher: '',
        size: blob.size,
        page: 0,
        path: '',
        charset: '',
        url: blobUrl,
        added: true,
        finish: false
      };
      
      localStorage.setItem('tempBook', JSON.stringify(bookData));
      localStorage.setItem('isTempBook', 'yes');
      
      // 构建 koodo-reader URL
      const format = book.format.toLowerCase();
      const routeMap: { [key: string]: string } = {
        epub: '/epub',
        pdf: '/pdf',
        mobi: '/mobi',
        azw3: '/azw3',
        azw: '/azw',
        txt: '/txt',
        md: '/md',
        docx: '/docx',
        fb2: '/fb2',
        html: '/html',
        htm: '/htm',
        cbz: '/cbz',
        cbr: '/cbr',
      };
      const readerRoute = routeMap[format] || '/epub';
      
      const params = new URLSearchParams({
        bookKey: 'book_' + bookId,
      });
      
      return '/reader/index.html#' + readerRoute + '?' + params.toString();
    } catch (err) {
      console.error('准备图书数据失败:', err);
      setError('准备图书数据失败');
      return '';
    }
  };

  const [readerUrl, setReaderUrl] = useState('');

  useEffect(() => {
    if (book) {
      getReaderUrl().then(url => {
        if (url) setReaderUrl(url);
      });
    }
  }, [book]);

  // 处理 TTS 消息
  useEffect(() => {
    console.log('[TTS] 组件挂载，注册消息监听器');

    const handleMessage = (event: MessageEvent) => {
      console.log('[TTS] ========== 收到消息 ==========');
      console.log('[TTS] 来源:', event.origin);
      console.log('[TTS] 数据类型:', typeof event.data);
      console.log('[TTS] 完整数据:', JSON.stringify(event.data));

      // 处理来自 iframe 的文本内容
      if (event.data?.type === 'TEXT_CONTENT') {
        if (event.data.text && event.data.text.trim().length > 0) {
          console.log('[TTS] ✓ 收到有效文本，长度:', event.data.text.length);
          console.log('[TTS] 文本前200字符:', event.data.text.substring(0, 200));
          speak(event.data.text);
        } else {
          console.warn('[TTS] ✗ 收到空文本');
        }
      } else {
        console.log('[TTS] 忽略非 TEXT_CONTENT 消息');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [speak]);

  // 最小化 TTS
  const handleMinimizeTTS = useCallback(() => {
    setMinimizedTTS(prev => !prev);
  }, []);

  // 关闭 TTS
  const handleCloseTTS = useCallback(() => {
    ttsService.stop();
    setShowTTS(false);
    setMinimizedTTS(false);
  }, [ttsService]);

  // TTS 按钮拖拽
  const handleBtnDragStart = useCallback((clientX: number, clientY: number) => {
    if (!btnRef.current) return;
    if (btnCentered) {
      const rect = btnRef.current.getBoundingClientRect();
      btnPosRef.current = { x: rect.left, y: rect.top };
      setBtnCentered(false);
      setBtnDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
    } else {
      setBtnDragOffset({ x: clientX - btnPosRef.current.x, y: clientY - btnPosRef.current.y });
    }
    setBtnDragging(true);
  }, [btnCentered]);

  useEffect(() => {
    if (!btnDragging) return;

    const handleMove = (clientX: number, clientY: number) => {
      const newX = clientX - btnDragOffset.x;
      const newY = clientY - btnDragOffset.y;
      const maxX = window.innerWidth - 48;
      const maxY = window.innerHeight - 48;
      btnPosRef.current = {
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      };
      if (btnRef.current) {
        btnRef.current.style.left = btnPosRef.current.x + 'px';
        btnRef.current.style.top = btnPosRef.current.y + 'px';
      }
    };

    const handleMouse = (e: MouseEvent) => handleMove(e.clientX, e.clientY);
    const handleTouch = (e: TouchEvent) => { e.preventDefault(); handleMove(e.touches[0].clientX, e.touches[0].clientY); };
    const handleEnd = () => setBtnDragging(false);

    document.addEventListener('mousemove', handleMouse);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouch, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouse);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouch);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [btnDragging, btnDragOffset]);

  const handleBtnClick = useCallback(() => {
    if (btnDragging) return;
    if (!isSupported) {
      alert('您的浏览器不支持语音朗读功能');
      return;
    }
    try {
      setShowTTS(prev => !prev);
      if (!showTTS) {
        setMinimizedTTS(false);
      }
    } catch (e) {
      console.warn('[TTS] 切换TTS面板失败:', e);
    }
  }, [isSupported, showTTS, btnDragging]);

  if (loading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#000',
        color: '#fff'
      }}>
        <div>加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '16px',
        background: '#000',
        color: '#fff'
      }}>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <button onClick={fetchBookInfo} className="btn btn-primary">
          重试
        </button>
      </div>
    );
  }

  if (!readerUrl) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#000',
        color: '#fff'
      }}>
        <div>加载中...</div>
      </div>
    );
  }

  // 阅读页面全屏模式（无 header/footer）
  return (
    <div 
      style={{
        height: '100vh',
        width: '100vw',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        background: '#000',
      }}
    >
      {/* TTS 控制器 */}
      {showTTS && (
        <TTSErrorBoundary fallback={<div style={{ position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '8px 16px', borderRadius: '8px', zIndex: 9999, fontSize: '13px' }}>TTS 不可用</div>}>
          <TTSControls
            ttsService={ttsService}
            onClose={handleCloseTTS}
            onMinimize={handleMinimizeTTS}
            isMinimized={minimizedTTS}
            speak={speak}
          />
        </TTSErrorBoundary>
      )}

      {/* TTS 触发按钮 */}
      <button
        ref={btnRef}
        onClick={handleBtnClick}
        onMouseDown={(e) => { if (!(e.target as HTMLElement).closest('.tts-btn')) handleBtnDragStart(e.clientX, e.clientY); }}
        onTouchStart={(e) => { if (!(e.target as HTMLElement).closest('.tts-btn')) { const t = e.touches[0]; handleBtnDragStart(t.clientX, t.clientY); } }}
        style={btnCentered
          ? { position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9998, background: showTTS ? 'rgba(79, 172, 254, 0.8)' : 'rgba(0, 0, 0, 0.7)', border: 'none', borderRadius: '50%', width: '48px', height: '48px', cursor: btnDragging ? 'grabbing' : 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)', touchAction: 'none' }
          : { position: 'fixed', left: btnPosRef.current.x + 'px', top: btnPosRef.current.y + 'px', zIndex: 9998, background: showTTS ? 'rgba(79, 172, 254, 0.8)' : 'rgba(0, 0, 0, 0.7)', border: 'none', borderRadius: '50%', width: '48px', height: '48px', cursor: btnDragging ? 'grabbing' : 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)', touchAction: 'none' }
        }
        title={showTTS ? '关闭 TTS' : '开启语音朗读'}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
      </button>

      <iframe
        ref={iframeRef}
        src={readerUrl}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          border: 'none',
          margin: 0,
          padding: 0,
        }}
        title="Koodo Reader"
      />
    </div>
  );
};

export default Reader;
